/**
 * Periods & Close — the control centre for sealing a month.
 *
 * A close is irreversible. Once a period is closed its snapshots *are* the record: every
 * report, every comparison, every "what did we say in November" answers from them, and there
 * is no supported way back. So this screen has one job, and it is not to provide a button.
 * It is to say, before anyone presses anything, exactly what is not ready and why.
 *
 * Each gate carries the number behind it, so nobody has to take a green tick on trust, and
 * each says whether it *blocks*. That distinction comes from the API rather than being
 * decided here — a close that waited for all 517 packages to report would never run, while
 * one that sealed over an in-flight application would apply an approved change to a record
 * nobody can amend. Both clients must agree about which is which.
 */
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { api, ApiError } from '@/api/client'
import { useViewPeriod } from '@/app/viewPeriod'
import { Icon } from '@/components/Icon'
import type { PeriodView } from '@/components/PeriodSwitcher'
import { Empty, Failed, Skeleton } from '@/components/states'
import { PeriodSchedule } from '@/features/periods/PeriodSchedule'
import { formatDate, formatMonth } from '@/lib/format'
import { gateLabel } from '@/lib/labels'

type Gate = {
  id: string
  label: string
  detail: string
  state: 'pass' | 'warn' | 'fail'
  value: number
  total: number
  blocking: boolean
  route: string | null
}

type Readiness = {
  period_code: string
  period_status: string
  can_close: boolean
  gates: Gate[]
}

export function Periods() {
  const { t, i18n } = useTranslation()
  const { code, setCode } = useViewPeriod()

  const periods = useQuery({
    queryKey: ['periods'],
    queryFn: ({ signal }) => api.get<PeriodView[]>('/periods?limit=24', signal),
    staleTime: 5 * 60_000,
  })

  const all = periods.data ?? []
  const live = all.find((period) => period.status === 'open') ?? null
  const subject = (code ? all.find((period) => period.code === code) : null) ?? live

  const readiness = useQuery({
    queryKey: ['readiness', subject?.code],
    queryFn: ({ signal }) =>
      api.get<Readiness>(`/periods/${subject!.code}/readiness`, signal),
    enabled: Boolean(subject),
  })

  if (periods.isError) {
    const error = periods.error
    return (
      <main className="page">
        <Failed
          message={error instanceof ApiError ? error.friendly : String(error)}
          onRetry={() => void periods.refetch()}
        />
      </main>
    )
  }

  return (
    <main className="page" data-testid="periods">
      <header className="head">
        <div>
          <p className="head__eyebrow">{t('nav.periods')}</p>
          <h1 className="head__title">{t('periods.title')}</h1>
        </div>
      </header>

      {periods.isPending ? (
        <Skeleton rows={6} />
      ) : all.length === 0 ? (
        <Empty title={t('period.none')} hint={t('periods.noneHint')} />
      ) : (
        <div className="periods">
          <section className="card card--pad" data-testid="close-checklist">
            <header className="matrix__head">
              <h2 className="card__title">
                {t('periods.checklist', {
                  month: subject ? formatMonth(subject.code, i18n.language) : '',
                })}
              </h2>
              <p className="card__caption">{t('periods.checklistSub')}</p>
            </header>

            {readiness.isPending ? (
              <Skeleton rows={5} />
            ) : readiness.data ? (
              <>
                <ul className="gates">
                  {readiness.data.gates.map((gate) => (
                    <li className="gate" key={gate.id} data-state={gate.state}>
                      <span className="gate__mark" aria-hidden="true">
                        <Icon
                          name={
                            gate.state === 'pass'
                              ? 'check'
                              : gate.state === 'fail'
                                ? 'close'
                                : 'warning'
                          }
                          size={13}
                          strokeWidth={2.6}
                        />
                      </span>
                      <span className="gate__body">
                        <span className="gate__label">
                          {gateLabel(t, gate.id, gate.label)}
                          {gate.blocking ? (
                            <span className="gate__blocking">{t('periods.blocking')}</span>
                          ) : null}
                        </span>
                        <span className="gate__detail">{gate.detail}</span>
                      </span>
                      <span className="visually-hidden">
                        {t(`periods.state.${gate.state}`)}
                      </span>
                    </li>
                  ))}
                </ul>

                <p
                  className={`gate__verdict${readiness.data.can_close ? ' gate__verdict--ready' : ''}`}
                  data-testid="close-verdict"
                >
                  {readiness.data.can_close
                    ? t('periods.ready')
                    : t('periods.blocked', {
                        count: readiness.data.gates.filter(
                          (gate) => gate.blocking && gate.state === 'fail',
                        ).length,
                      })}
                </p>

                {/* No close button. Sealing a month is a scheduled, transactional operation
                    that writes a snapshot of every governed table; running it from a click on
                    a web page is how a period ends up half-sealed when a request times out.
                    This screen reports readiness; the worker performs the close. */}
                <p className="card__note">{t('periods.howClosed')}</p>
              </>
            ) : null}
          </section>

          <section className="card card--pad">
            <h2 className="card__title">{t('periods.history')}</h2>
            <div className="grid__scroll" data-scroll="1">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">{t('periods.month')}</th>
                    <th scope="col">{t('periods.status')}</th>
                    <th scope="col">{t('periods.dataDate')}</th>
                    <th scope="col" className="table__numeric">
                      {t('periods.reported')}
                    </th>
                    <th scope="col" className="table__numeric">
                      {t('periods.snapshotRows')}
                    </th>
                    <th scope="col">
                      <span className="visually-hidden">{t('grid.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {all.map((period) => (
                    <tr key={period.code} data-testid="period-row">
                      <td className="numeric">{formatMonth(period.code, i18n.language)}</td>
                      <td>
                        <span className={`chip chip--${chipFor(period.status)}`}>
                          {t(`period.${period.status}`, period.status)}
                        </span>
                      </td>
                      <td className="table__muted">
                        {formatDate(period.data_date, i18n.language)}
                      </td>
                      <td className="table__numeric numeric">
                        {period.packages_reported === null
                          ? '—'
                          : `${period.packages_reported} / ${period.packages_expected ?? '—'}`}
                      </td>
                      <td className="table__numeric numeric">
                        {period.snapshot_row_count === null
                          ? '—'
                          : period.snapshot_row_count.toLocaleString(i18n.language)}
                      </td>
                      <td className="table__numeric">
                        {period.status === 'open' ? null : (
                          <button
                            type="button"
                            className="button button--quiet button--small"
                            onClick={() => setCode(period.code)}
                            data-testid="period-view"
                          >
                            {t('periods.view')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* The other half of the period lifecycle. Closing had a screen and opening had
              nothing, though it is the half people actually wait on each month. */}
          <PeriodSchedule />
        </div>
      )}
    </main>
  )
}

function chipFor(status: string): string {
  if (status === 'open') return 'good'
  if (status === 'closed') return 'info'
  return 'neutral'
}
